import { http } from 'app/lib/axios';
import type { Channel } from 'app/types/chat';
import { getOneChannelDefaultInclude } from 'app/types/chat';
import { channelKeys } from 'app/network/keys';
import { queryClient } from 'app/lib/react-query';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import type { TokenPagination } from 'app/types/network';
import { isMobileNative } from 'app/utils/web-helpers';

export type PushNotifHandlingSource = 'apnsBackground' | 'onRemoteChatMessage' | 'onRemoteMessage';

export async function updateBadgeData(
  payload: { channel_url?: string; unread_message_count?: string },
  source: PushNotifHandlingSource,
) {
  if (payload.unread_message_count) {
    // This is just an approximate count; will fetch and update the badge with the appropriate number below
    await setBadgeCount(parseInt(payload.unread_message_count), `${source}1`);
  }

  if (payload.channel_url) {
    // This is a DM; update channel(s) info
    await reloadDataForChannel(payload.channel_url, source);
  }

  if (!payload.unread_message_count && Platform.OS === 'ios') {
    // Only iOS supports badges
    // The only way to get the correct number to show on the badge is to fetch the channels from the server!
    // Do it as fast as possible, when the received notification is for a DM, but throttle when it's (possibly) a broadcast, in order to not hammer the server with too many requests simultaneously...
    const randomWait = payload.channel_url ? 0 : Math.random() * 15;
    if (!payload.channel_url) {
      console.log(`[${source}] Sleeping ${randomWait.toFixed(0)} seconds...`);
    }
    await new Promise((resolve) => setTimeout(resolve, randomWait * 1000));
    const totalUnreadMessages = await getUnseenMessagesCount(source);

    await setBadgeCount(totalUnreadMessages, `${source}2`);
  }
}

export async function reloadDataForChannel(channelUrl: string, source: PushNotifHandlingSource) {
  console.log(`Loading channel (${channelUrl}) detail from API...`);
  const res = await http.get<Channel>(
    `/chat/channels/${channelUrl}?include=${getOneChannelDefaultInclude.join(',')}&source=${source}`,
  );
  const queryKey = channelKeys.detail(channelUrl, getOneChannelDefaultInclude);
  queryClient.setQueryData(queryKey, res.data);

  return reloadDataForChannels(source);
}

export async function reloadDataForChannels(source: PushNotifHandlingSource) {
  console.log(`Loading channels list from API...`);

  const resList = await http.get<TokenPagination<Channel>>(`/chat/channels`, {
    params: { archived: false, source },
  });
  const queryKeyList = channelKeys.list({ search: undefined, archived: false });
  queryClient.setQueryData(queryKeyList, { pages: [resList.data], pageParams: [resList.data.next] });

  return resList.data.results.reduce((acc, c) => acc + c.unread_message_count, 0);
}

export async function getUnseenMessagesCount(source: PushNotifHandlingSource) {
  console.log(`Loading # of unseen messages from API...`);

  const res = await http.get<{ unread_count: number }>('/chat/unread', {
    params: { source },
  });

  return res.data.unread_count;
}

export async function setBadgeCount(badgeCount: number, source: string) {
  if (!isMobileNative) return;
  console.log(`[${source}] setBadgeCount`, badgeCount);
  const write = Notifications.setBadgeCountAsync(badgeCount);
  // Badge metrics are best effort, including during logout after credentials are cleared.
  void http.post('/notifications/metrics/badge', { badgeCount, source }).catch(() => undefined);
  return write;
}
